/** @jsxImportSource @emotion/react */
import { formatErrorResponse } from '@quickbit/backoffice-core-frontend';
import {
  List,
  Typography,
  UtilityButton,
  Snackbar,
  useTheme,
  Icon,
} from '@quickbit/qb-design-system';
import { useEffect, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { Order, OrderStatus } from 'tabs/types';
import { CallbackType, resendCallback, simulateCallback } from '../api';
import { useGenerateProofPDF } from './hooks';

interface Props {
  order: Order;
  refresh: () => void;
  openErrorModal: () => void;
  openCallbackModal: () => void;
  openDetailsModel: () => void;
  openRefundOrderModal: () => void;
}

const NOT_ALLOWED_RESEND_STATUSES: OrderStatus[] = ['PENDING', 'REFUNDING'];

interface Action {
  label: string;
  onClick: () => void;
}

interface SnackbarInfo {
  variant: 'success' | 'danger';
  message: string | string[];
}

export const ActionsDropdown = ({
  order,
  refresh,
  openErrorModal,
  openCallbackModal,
  openDetailsModel,
  openRefundOrderModal,
}: Props) => {
  const [snackbarInfo, setSnackbarInfo] = useState<SnackbarInfo>();
  const { spacing, palette } = useTheme();

  const { generateProofPDF } = useGenerateProofPDF();
  const {
    uuid: orderId,
    externalStatus: status,
    useOnrampFlow,
    useProcessingFlow,
  } = order;

  const isPayOrder = !useOnrampFlow && !useProcessingFlow;

  const [
    { value: simulateValue, error: simulateError, loading: simulateLoading },
    simulate,
  ] = useAsyncFn(async (type: CallbackType) => {
    await simulateCallback({ orderId, type });
    refresh();
    return true;
  });

  const [
    { value: resendValue, error: resendError, loading: resendLoading },
    resend,
  ] = useAsyncFn(async () => {
    await resendCallback(orderId);
    refresh();
    return true;
  });

  const simulateActions: Action[] = [
    {
      label: 'Simulate error',
      onClick: () => simulate('ERROR_OCCURRED'),
    },
  ];

  const statusMapping: Record<string, CallbackType[]> = {
    PENDING: ['COMPLETED', 'CANCELLED'],
    PARTIALLY_REFUNDED: ['REFUNDED'],
    NOTICED: ['COMPLETED', 'REFUNDED'],
  };

  if (!useOnrampFlow && !useProcessingFlow) {
    statusMapping.PENDING.push('NOTICED');
    statusMapping.PENDING.push('REFUNDED');
  }

  if (status in statusMapping) {
    statusMapping[status].forEach((newStatus) => {
      simulateActions.push({
        label: `Simulate ${newStatus.toLowerCase()}`,
        onClick: () => simulate(newStatus),
      });
    });
  }

  const actions: Action[] = [
    { label: 'View details', onClick: openDetailsModel },
  ];

  if (process.env.REACT_APP_ENVIRONMENT !== 'production') {
    actions.push(...simulateActions);
  }
  if (!NOT_ALLOWED_RESEND_STATUSES.includes(status)) {
    actions.push({
      label: 'Resend callback',
      onClick: () => resend(),
    });
  }
  actions.push({
    label: 'View callbacks',
    onClick: openCallbackModal,
  });
  if (
    isPayOrder &&
    (order.externalStatus === 'COMPLETED' ||
      order.externalStatus === 'PARTIALLY_REFUNDED')
  ) {
    actions.push({
      label: 'Refund order',
      onClick: () => {
        openRefundOrderModal();
      },
    });
  }
  if (order.errors.length > 0) {
    actions.push({
      label: 'View errors',
      onClick: openErrorModal,
    });
  }
  if (isPayOrder && order.externalStatus === 'COMPLETED') {
    actions.push({
      label: 'Download proof',
      onClick: () => {
        generateProofPDF({
          order: order,
        });
      },
    });
  }

  useEffect(() => {
    if (simulateLoading) {
      setSnackbarInfo(undefined);
    } else if (simulateError) {
      setSnackbarInfo({
        variant: 'danger',
        message: formatErrorResponse(simulateError),
      });
    } else if (simulateValue) {
      setSnackbarInfo({
        variant: 'success',
        message: `Callback simulated for order ${orderId}`,
      });
    }
  }, [simulateError, simulateLoading, setSnackbarInfo, orderId, simulateValue]);

  useEffect(() => {
    if (resendLoading) {
      setSnackbarInfo(undefined);
    } else if (resendError) {
      setSnackbarInfo({
        variant: 'danger',
        message: formatErrorResponse(resendError),
      });
    } else if (resendValue) {
      setSnackbarInfo({
        variant: 'success',
        message: `Callback resent for order ${orderId}`,
      });
    }
  }, [resendError, resendLoading, setSnackbarInfo, orderId, resendValue]);

  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.xxxs,
      }}
    >
      <UtilityButton
        css={{ padding: `calc(${spacing.xxxs} / 4)` }}
        text="Actions"
        variant="text"
        dropdown={({ close }) => (
          <List css={{ minWidth: 'unset' }}>
            {actions.map(({ label, onClick }) => (
              <div
                key={label}
                onClick={() => {
                  onClick();
                  close();
                }}
                css={{
                  padding: `${spacing.xxxs} ${spacing.xxs}`,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  ':hover': {
                    background: palette.grey.x100,
                  },
                }}
              >
                <Typography>{label}</Typography>
              </div>
            ))}
          </List>
        )}
      />
      {order.errors.length > 0 && <Icon name="error" />}
      {snackbarInfo && (
        <Snackbar
          variant={snackbarInfo.variant}
          message={snackbarInfo.message}
          open={!!snackbarInfo}
          onClose={() => {
            setSnackbarInfo(undefined);
          }}
        />
      )}
    </div>
  );
};
